import * as React from 'react';
import _ from 'lodash';
import type {
  MapStateToProps,
  MapDispatchToProps,
  ThunkAction,
} from 'types/redux';
import {
  FontPicker as ExternalFontPicker,
  Footer,
  FontWeightDropdown,
  Header,
  RecentFonts,
  type FontPickerProps as WixFontPickerProps,
  type TargetBISelectOptions,
  type FontGroup,
  type FontGroupName,
  type FontOption,
} from '@wix/font-picker';
import { fontPickerActionsSrc38Evid3412 } from '@wix/bi-logger-editor-data/v2';

import { biLogger } from '#packages/util';
import type { EditorAPI } from '@/editorAPI';
import * as util from '@/util';
import * as stateManagement from '@/stateManagement';
import * as coreBi from '@/coreBi';
import type { BiEventFields } from 'types/bi';
import { type CompRef } from 'types/documentServices';

import { getFontGroups } from './getFontGroups';
import { getMissingFontName } from './getMissingFontName';

export { type FontGroup, type FontGroupName, type FontOption };

export type Origin = 'keyboard' | 'mouse' | undefined;

interface FontPickerStateProps {
  fontsGroups: FontGroup[];
  recentFonts: string[];
}

interface FontPickerDispatchProps {
  getMissingFontName: (fontFamily: string) => string;
  onRecentFontChange: (fonts: string[]) => void;
  onAddLanguagesClick: () => void;
  onUploadFontsClick: () => void;
  onSearch: (value: string) => void;
  onToggleDispatch: (isOpened: boolean) => void;
  onChangeDispatch: (
    family: string,
    weight: number | undefined,
    biData: TargetBISelectOptions,
  ) => void;
  onWeightsDropdownToggle: (isOpened: boolean) => void;
}

export interface OnPreviewBIData {
  origin: Origin;
  showSubmenu?: boolean;
  hasSingleWeight?: boolean;
}

export interface FontPickerBiData {
  panelName?: string;
  compRef?: CompRef;
  origin?: string;
}

interface FontPickerOwnProps {
  family: string;
  weight: number;
  showWeightPicker?: boolean;
  showWeightOptions?: boolean;
  biData?: FontPickerBiData;
  getFontSuffix: WixFontPickerProps['getFontSuffix'];
  onAddLanguagesClick: () => void;
  onUploadFontsClick: () => void;
  onChange: (family: string, weight: number | undefined) => void;
  onPreview?: (
    family: string,
    weight: number | undefined,
    biData: OnPreviewBIData,
  ) => void;
  onToggle?: () => void;
}

type FontPickerProps = FontPickerOwnProps &
  FontPickerDispatchProps &
  FontPickerStateProps;

type FontPickerActionsSrc38Evid3412Params = Parameters<
  typeof fontPickerActionsSrc38Evid3412
>[0];

const recentFontsKey = 'site-recent-fonts-v2';

const FontPickerPure: React.FC<FontPickerProps> = (props) => {
  const {
    family,
    weight,
    fontsGroups,
    showWeightPicker = true,
    showWeightOptions = true,
    recentFonts,
    getFontSuffix,
    getMissingFontName,
    onChange,
    onChangeDispatch,
    onPreview,
    onToggle,
    onToggleDispatch,
    onRecentFontChange,
    onAddLanguagesClick,
    onUploadFontsClick,
    onSearch,
    onWeightsDropdownToggle,
  } = props;

  const handleRecentFontsChange = React.useCallback(
    (fonts: string[]) => {
      onRecentFontChange(fonts);
    },
    [onRecentFontChange],
  );

  const handlePreview: WixFontPickerProps['onPreview'] = React.useCallback(
    (family, weight, biData) => {
      if (onPreview) {
        onPreview(family, weight, {
          origin: biData?.fontItemNavigationOrigin,
          showSubmenu: biData?.showSubmenu,
          hasSingleWeight: !biData?.hasWeights,
        });
      }
    },
    [onPreview],
  );

  const handleToggle = React.useCallback(
    (isOpened: boolean) => {
      onToggle();
      onToggleDispatch(isOpened);
    },
    [onToggle, onToggleDispatch],
  );

  const handleChange: WixFontPickerProps['onChange'] = React.useCallback(
    (family, weight, biData) => {
      onChange(family, weight);
      onChangeDispatch(family, weight, biData);
    },
    [onChange, onChangeDispatch],
  );

  const weightsDropdown = showWeightPicker ? <FontWeightDropdown /> : undefined;

  return (
    <ExternalFontPicker
      family={family}
      weight={weight}
      fontsGroups={fontsGroups}
      getFontSuffix={getFontSuffix}
      getMissingFontName={getMissingFontName}
      shouldHideWeightsOptionsInList={!showWeightOptions}
      header={<Header />}
      recentFontsSection={
        <RecentFonts
          fonts={recentFonts}
          onFontsChange={handleRecentFontsChange}
        />
      }
      footer={
        <Footer
          onAddLanguagesClick={onAddLanguagesClick}
          onUploadFontsClick={onUploadFontsClick}
        />
      }
      weightDropdown={weightsDropdown}
      onPickerToggle={handleToggle}
      onChange={handleChange}
      onPreview={handlePreview}
      onSearchInputChange={onSearch}
      onWeightsDropdownToggle={onWeightsDropdownToggle}
    />
  );
};

const getEditorAPI: ThunkAction = (_dispatch, _getState, { editorAPI }) =>
  editorAPI;

const mapStateToProps: MapStateToProps<
  FontPickerStateProps,
  FontPickerOwnProps
> = ({ editorAPI }) => {
  const recentFonts: string[] =
    stateManagement.userPreferences.selectors.getSiteUserPreferences(
      recentFontsKey,
    )(editorAPI.store.getState()) ?? [];
  return {
    fontsGroups: getFontGroups(editorAPI),
    recentFonts,
  };
};

const mapDispatchToProps: MapDispatchToProps<
  FontPickerDispatchProps,
  FontPickerOwnProps
> = (dispatch, ownProps) => {
  const editorAPI: EditorAPI = dispatch(getEditorAPI);

  const sendBI = (evid: number, props: BiEventFields = {}) => {
    dispatch(
      stateManagement.bi.actions.event(
        { evid },
        {
          ...props,
          panel_name: ownProps.biData?.panelName,
          origin: 'font_dropdown',
          extras: JSON.stringify({
            isNewFontPicker: true,
          }),
        },
      ),
    );
  };

  const sendFontPickerActionsEvent = (
    params: FontPickerActionsSrc38Evid3412Params,
  ) => {
    const compRef = ownProps.biData?.compRef;

    biLogger.report(
      fontPickerActionsSrc38Evid3412({
        ...params,
        component_type: compRef
          ? editorAPI.components.getType(compRef)
          : undefined,
        component_id: compRef?.id,
        panel_name: ownProps.biData?.panelName,
        areaName: ownProps.biData?.origin,
      }),
    );
  };

  const sendSearchEventDebounced = _.debounce((value: string) => {
    sendFontPickerActionsEvent({
      target: 'font',
      actionName: 'search_font',
      targetValue: value,
    });
  }, 3000);

  return {
    getMissingFontName: (fontFamily) => {
      return getMissingFontName(fontFamily, editorAPI);
    },
    onRecentFontChange: (fonts: string[]): void => {
      editorAPI.store.dispatch(
        stateManagement.userPreferences.actions.setSiteUserPreferences(
          recentFontsKey,
          fonts,
        ),
      );
    },
    onAddLanguagesClick: () => {
      if (ownProps.onAddLanguagesClick) {
        ownProps.onAddLanguagesClick();
      } else {
        sendBI(
          coreBi.events.textControls.Text_Panel_Font_Language_Link_Click.evid,
        );

        editorAPI.panelManager.openPanel(
          'panels.toolPanels.languageSupport',
          {},
          true,
        );
      }
    },
    onUploadFontsClick: () => {
      if (ownProps.onUploadFontsClick) {
        ownProps.onUploadFontsClick();
      } else {
        sendBI(
          coreBi.events.textControls.Text_Panel_Font_Upload_Link_Click.evid,
        );

        editorAPI.panelManager.openPanel(
          'panels.toolPanels.fontsUploadPanel',
          {},
          true,
        );
      }
    },
    onSearch: (value) => {
      sendSearchEventDebounced(value);
    },
    onToggleDispatch: (isOpened: boolean) => {
      if (isOpened) {
        sendFontPickerActionsEvent({
          target: 'font',
          actionName: 'open_dropdown',
        });
      }

      if (ownProps.onToggle) {
        ownProps.onToggle();
      }
    },
    onWeightsDropdownToggle: (isOpened: boolean) => {
      if (isOpened) {
        sendFontPickerActionsEvent({
          target: 'font_weight',
          actionName: 'open_dropdown',
        });
      }
    },
    onChangeDispatch: (
      family: string,
      weight: number | undefined,
      biData: TargetBISelectOptions,
    ) => {
      sendFontPickerActionsEvent({
        target: biData.target,
        actionName: 'choose_target_value',
        isUploadedFont: `${biData.isUploadedFont}`,
        targetValue: biData.targetValue,
        viewType: 'viewType' in biData ? biData.viewType : undefined,
        origin: biData.origin,
        fontState: JSON.stringify({
          font: family,
          fontWeight: `${weight}`,
        }),
        hasSingleWeight: `${biData.weightsLength <= 1}`,
      });
    },
  };
};

const FontPickerConnected = util.hoc.connect(
  util.hoc.STORES.EDITOR_API,
  mapStateToProps,
  mapDispatchToProps,
)(FontPickerPure);

export { FontPickerConnected as FontPicker };
